import {
  CheckCircleIcon,
  ClockIcon,
  FireIcon,
  RefreshIcon,
  ShareIcon,
} from '@heroicons/react/outline'

import { GameMode } from '../../lib/gameStorage'
import { PlayerStats } from '../../lib/playerStats'
import { BaseModal } from './BaseModal'

type Props = {
  isOpen: boolean
  handleClose: () => void
  stats: PlayerStats
  isComplete: boolean
  mode: GameMode
  solvedRows: number
  mistakes: number
  hintsUsed: number
  elapsed: string
  currentDailyStreak: number
  onShare: () => void
  onPlayAnother: () => void
}

const formatBestTime = (seconds: number | null) => {
  if (seconds === null) return '—'
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`
}

export const ProgressModal = ({
  isOpen,
  handleClose,
  stats,
  isComplete,
  mode,
  solvedRows,
  mistakes,
  hintsUsed,
  elapsed,
  currentDailyStreak,
  onShare,
  onPlayAnother,
}: Props) => (
  <BaseModal
    title={isComplete ? 'Board complete' : 'Score sheet'}
    isOpen={isOpen}
    handleClose={handleClose}
  >
    {isComplete && (
      <div className="modal-celebration">
        <CheckCircleIcon />
        <p>
          You solved this {mode} square in <strong>{elapsed}</strong> with{' '}
          <strong>{mistakes}</strong> {mistakes === 1 ? 'mistake' : 'mistakes'}{' '}
          and <strong>{hintsUsed}</strong> {hintsUsed === 1 ? 'hint' : 'hints'}.
        </p>
      </div>
    )}

    {!isComplete && (
      <p className="modal-progress-copy">
        You have solved <strong>{solvedRows} of 5 rows</strong>. Your board is
        saved, so you can close the game and return anytime.
      </p>
    )}

    <div className="stats-grid">
      <div>
        <strong>{stats.totalSolved}</strong>
        <span>Squares solved</span>
      </div>
      <div>
        <strong>{stats.practiceSolved}</strong>
        <span>Practice wins</span>
      </div>
      <div>
        <strong>{currentDailyStreak}</strong>
        <span>Daily streak</span>
      </div>
      <div>
        <strong>{formatBestTime(stats.bestTimeSeconds)}</strong>
        <span>Best time</span>
      </div>
    </div>

    <div className="stats-details">
      <span>
        <FireIcon />
        Best streak <strong>{stats.bestDailyStreak}</strong>
      </span>
      <span>
        <ClockIcon />
        Daily solves <strong>{stats.dailySolved}</strong>
      </span>
    </div>

    {isComplete && (
      <div className="modal-actions">
        <button
          className="modal-action modal-action--secondary"
          onClick={onShare}
        >
          <ShareIcon />
          Share result
        </button>
        <button
          className="modal-action modal-action--primary"
          onClick={onPlayAnother}
        >
          <RefreshIcon />
          Play another
        </button>
      </div>
    )}
  </BaseModal>
)
